import React from 'react';
import { cn } from '../lib/utils';
import { theme } from '../config/theme';

const Card = React.forwardRef(({
  children,
  variant = 'default',
  padding = 'md',
  shadow = 'md',
  hover = false,
  className,
  ...props
}, ref) => {
  const baseClasses = 'border rounded-xl transition-all duration-200 will-change-transform';
  
  const variants = {
    default: '',
    elevated: 'shadow-lg',
    outlined: 'border-2',
    ghost: 'bg-transparent border-transparent',
    glass: '',
  };
  
  const paddings = {
    none: '',
    sm: 'p-3 sm:p-4',
    md: 'p-4 sm:p-6',
    lg: 'p-5 sm:p-8',
    xl: 'p-6 sm:p-10',
  };
  
  const shadows = {
    none: '',
    sm: 'shadow-sm',
    md: 'shadow-md',
    lg: 'shadow-lg',
    xl: 'shadow-xl',
  };
  
  const hoverClasses = hover ? 'hover:-translate-y-[2px] cursor-pointer' : '';
  
  const classes = cn(
    baseClasses,
    variants[variant],
    paddings[padding],
    shadows[shadow],
    hoverClasses,
    className
  );
  
  const baseStyle = {
    background: theme.bgCard,
    borderColor: theme.border,
    boxShadow: shadow === 'none' ? 'none' : theme.shadow,
    transition: 'transform .18s ease, box-shadow .2s ease'
  };
  const glassStyle = variant === 'glass' ? {
    background: theme.glassBg,
    borderColor: theme.glassBorder,
    backdropFilter: `saturate(160%) blur(${theme.glassBlur})`,
    WebkitBackdropFilter: `saturate(160%) blur(${theme.glassBlur})`
  } : null;

  return (
    <div
      ref={ref}
      className={classes}
      style={{ ...(glassStyle || baseStyle), boxShadow: shadow === 'none' ? 'none' : theme.shadow, transition: 'transform .18s ease, box-shadow .2s ease' }}
      {...props}
    >
      {children}
    </div>
  );
});

// Подкомпоненты для структурирования карточки
Card.Header = React.forwardRef(({ children, className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex items-center justify-between mb-4', className)}
    {...props}
  >
    {children}
  </div>
));

Card.Title = React.forwardRef(({ children, className, ...props }, ref) => (
  <h3
    ref={ref}
    className={cn('text-base sm:text-lg font-semibold text-[color:var(--text)]', className)}
    {...props}
  >
    {children}
  </h3>
));

Card.Subtitle = React.forwardRef(({ children, className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn('text-sm text-[color:var(--muted)]', className)}
    {...props}
  >
    {children}
  </p>
));

Card.Content = React.forwardRef(({ children, className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex-1', className)}
    {...props}
  >
    {children}
  </div>
));

Card.Footer = React.forwardRef(({ children, className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex items-center justify-between pt-4 mt-4 border-t border-[color:var(--border)]', className)}
    {...props}
  >
    {children}
  </div>
));

Card.displayName = 'Card';

export default Card;
